import { useTutorialGuidanceStore } from '@/stores/tutorial-guidance';
import { type TutorialGuidanceDto, TriggerType } from '@onrunning/backstage-models';
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useUserStore } from '../stores/user';
import { useTutorialGuidanceData } from './useTutorialGuidanceData';
import { useTutorialGuidanceTour } from './useTutorialGuidanceTour';

const WAIT_TO_FIRE = 2000;

export function useTutorialGuidanceManager() {
  const route = useRoute();
  const tutorialStore = useTutorialGuidanceStore();
  const tutorialGuidanceData = useTutorialGuidanceData();
  const userStore = useUserStore();
  const { startGuidanceTour } = useTutorialGuidanceTour();

  // Track active listeners and observers for cleanup
  const activeListeners = ref<Array<{ element: Element; handler: EventListener }>>([]);
  const activeObservers = ref<MutationObserver[]>([]);

  watch(
    () => [route.name, userStore.hasManuallySelectedAnAccount],
    async ([toName, hasManuallySelectedAnAccount]) => {
      if (!hasManuallySelectedAnAccount || !toName) {
        return;
      }

      cleanupTutorialEvents();
      await initializeTutorials(toName as string);
    },
  );

  const getTutorialsForPage = (pageName: string | symbol) => {
    return tutorialStore.guidances.filter((tutorial) => tutorial.pageName === pageName);
  };

  onBeforeUnmount(() => {
    cleanupTutorialEvents();
  });

  const startTour = (tutorial: TutorialGuidanceDto, onDestroyed?: () => void) => {
    startGuidanceTour(tutorial, {
      allowClose: false,
      onDestroyed: () => {
        tutorialGuidanceData.completeCurrentGuidance(tutorial._id);
        if (onDestroyed) {
          onDestroyed();
        }
      },
    });
  };

  const handleLoadTrigger = (tutorial: TutorialGuidanceDto) => {
    const onElementFound = () => setTimeout(() => startTour(tutorial), WAIT_TO_FIRE);
    waitForElement(tutorial.elementId, onElementFound);
  };

  const handleClickTrigger = (tutorial: TutorialGuidanceDto) => {
    if (!tutorial.elementId) return;
    waitForElement(tutorial.elementId, (el) => el && attachClickHandler(el, tutorial));
  };

  const processTutorial = (tutorial: TutorialGuidanceDto) => {
    if (tutorial.triggerType === TriggerType.Load) {
      handleLoadTrigger(tutorial);
    } else if (tutorial.triggerType === TriggerType.Click) {
      handleClickTrigger(tutorial);
    }
  };

  const initializeTutorials = async (pageName: string) => {
    if (userStore.isAuthenticated && !tutorialStore.fetched) {
      await tutorialGuidanceData.fetchUnviewedGuidance();
    }

    if (tutorialStore.guidances.length === 0) return;

    const tutorials = getTutorialsForPage(pageName);
    tutorials.forEach(processTutorial);
  };

  function waitForElement(selector: string | undefined, callback: (el?: Element) => void) {
    if (!selector) {
      callback();

      return;
    }

    nextTick(() => {
      const existingElement = document.querySelector(selector);
      if (existingElement) {
        callback(existingElement);

        return;
      }

      const observer = new MutationObserver((_mutations, obs) => {
        const element = document.querySelector(selector);
        if (element) {
          obs.disconnect();
          callback(element);
          const index = activeObservers.value.indexOf(obs);
          if (index > -1) activeObservers.value.splice(index, 1);
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });
      activeObservers.value.push(observer);
    });
  }

  function attachClickHandler(element: Element, tutorial: TutorialGuidanceDto) {
    const clickHandler = () => {
      startTour(tutorial, () => {
        // Remove listener immediately after first click to prevent re-triggering
        element.removeEventListener('click', clickHandler, true);
      });
    };

    // Use capture phase (true) to catch clicks before child elements can stopPropagation
    element.addEventListener('click', clickHandler, true);

    // Track for cleanup
    activeListeners.value.push({ element, handler: clickHandler });
  }

  function cleanupTutorialEvents() {
    activeListeners.value.forEach(({ element, handler }) => {
      element.removeEventListener('click', handler, true);
    });
    activeListeners.value = [];

    activeObservers.value.forEach((observer) => {
      observer.disconnect();
    });
    activeObservers.value = [];
  }

  return {
    cleanupTutorialEvents,
  };
}
